import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import cn from 'clsx';
import { Button, Modal } from 'flowbite-react';
import { usePostHog } from 'posthog-js/react';
import {
  KeyboardEventHandler,
  ReactElement,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useLoaderData, useSearchParams } from 'react-router-dom';

import { postFeedback } from 'src/api/methods';
import { queries } from 'src/api/queries';
import { ChatBubble, ChatBubbleProps } from 'src/components/chat-bubble';
import { Player, VideoJS } from 'src/components/video-js';
import { usePosthogAttributionDataFromLoader } from 'src/routes/video-landing-page/hooks';
import { LocalizedStrings, VideosContext } from 'src/routes/video-landing-page/shared';
import { isUseCaseAbandonedCart, removeNulls, sleep, useParsedHtmlString } from 'src/utils';

import classes from './videos.module.css';

function HeartVideoIcon(props: { didHeart: boolean; onHeart: () => void }): ReactElement {
  const { didHeart, onHeart } = props;
  return (
    <button
      type='button'
      className='cursor-pointer rounded-full px-2 py-1 text-2xl text-gray-500 hover:bg-gray-200 hover:text-red-500'
      onClick={onHeart}
    >
      {didHeart || <FontAwesomeIcon icon={regular('heart')} />}
      {didHeart && <FontAwesomeIcon icon={solid('heart')} className='text-red-600' />}
    </button>
  );
}

function VideoPosterMessage(props: {
  onClick: () => void;
  onHeart: () => void;
  didHeart: boolean;
  animateCirclePlay: boolean;
  posterImage: string | undefined;
}): ReactElement {
  const { onClick, onHeart, didHeart, animateCirclePlay, posterImage } = props;
  return (
    <>
      <div onClick={onClick} className='relative cursor-pointer'>
        <img alt='video thumbnail' src={posterImage} className='block h-auto w-full object-cover' />
        <div className='absolute bottom-1/2 top-1/2 flex w-full items-center justify-center'>
          <FontAwesomeIcon
            icon={regular('circle-play')}
            size='3x'
            className={animateCirclePlay ? 'fa-beat text-white' : 'text-white'}
          />
        </div>
      </div>
      <div className='absolute -right-14 bottom-1/2 top-1/2 flex items-center justify-center'>
        <HeartVideoIcon didHeart={didHeart} onHeart={onHeart}></HeartVideoIcon>
      </div>
    </>
  );
}

function ProductImagesMessage(props: {
  href: string;
  productImageUrl1: string | undefined;
  productImageUrl2: string | undefined;
  onClick: () => void;
}): ReactElement {
  const { href, productImageUrl1, productImageUrl2, onClick } = props;
  const columnsClassName = productImageUrl1 && productImageUrl2 ? 'columns-2' : 'columns-1';
  return (
    <>
      <div className='relative cursor-pointer'>
        <div className={columnsClassName}>
          {productImageUrl1 && (
            <div className='flex-column'>
              <a
                target='blank'
                href={href}
                onClick={onClick}
                className='underline decoration-sky-500 decoration-solid hover:decoration-blue-500'
              >
                <img
                  alt='product image 1'
                  src={productImageUrl1}
                  className='block h-auto w-full object-cover'
                />
              </a>
            </div>
          )}
          {productImageUrl2 && (
            <div className='flex-column'>
              <a
                target='blank'
                href={href}
                onClick={onClick}
                className='underline decoration-sky-500 decoration-solid hover:decoration-blue-500'
              >
                <img
                  alt='product image 2'
                  src={productImageUrl2}
                  className='block h-auto w-full object-cover'
                />
              </a>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function CtaMessage(props: {
  message: ReactNode;
  href: string;
  onClick: () => void;
}): ReactElement {
  const { message, href, onClick } = props;
  return (
    <a
      target='blank'
      href={href}
      onClick={onClick}
      className='underline decoration-sky-500 decoration-solid hover:decoration-blue-500'
    >
      <span className='fa-layer'>
        {message} <FontAwesomeIcon icon={solid('arrow-up-right-from-square')} />
      </span>
    </a>
  );
}

type Message = Omit<ChatBubbleProps, 'children'> &
  (
    | {
        messageType: 'string';
        message: string;
      }
    | {
        messageType: 'cta';
        message: string;
        href: string;
      }
  );

export function Videos(): ReactElement {
  const {
    object,
    useCaseInfo,
    customerFirstName,
    videoPageInfo,
    storeInfo,
    ctaUrl,
    hyperlinkVideoUrl,
    phone,
    email,
    deliveryMethod,
    couponCode,
  } = useLoaderData() as VideosContext;

  const posthog = usePostHog();
  const posthogData = usePosthogAttributionDataFromLoader();

  const messagesDiv = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const userDidSendMessage = useRef(false);
  const didPromptUserToReply = useRef<{ heart: boolean; videoEnd: boolean }>({
    heart: false,
    videoEnd: false,
  });
  const videoPlayer = useRef<Player | null>(null);

  useLayoutEffect(() => document.documentElement.classList.add('video-landing-page'));
  const [textAreaValue, setTextAreaValue] = useState('');
  const [openVideoModal, setOpenVideoModal] = useState(false);
  const [didHeart, setDidHeart] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [animateCTA, setAnimateCTA] = useState(false);
  const [animateCirclePlay, setAnimateCirclePlay] = useState(true);
  const [isAbandonedCart] = useState(isUseCaseAbandonedCart(useCaseInfo));

  const [searchParams] = useSearchParams();
  const { data: productImages } = useQuery({
    ...queries.shopifyCartProductImages(
      storeInfo.key_name,
      searchParams.get('shopify_checkout_id'),
      searchParams.get('shopify_checkout_token'),
      [searchParams.get('shopify_product_id_0'), searchParams.get('shopify_product_id_1')].filter(
        removeNulls,
      ),
    ),
    placeholderData: { product_images: [] },
    enabled: isUseCaseAbandonedCart(useCaseInfo),
  });

  const messageText = useParsedHtmlString(
    (videoPageInfo.message_text ?? '')
      .replace('{ coupon_code }', '{coupon_code}')
      .replace('{coupon_code}', couponCode ?? ''),
  );
  const supportingText = useParsedHtmlString(
    videoPageInfo.supporting_text
      ?.replace('{ coupon_code }', '{coupon_code}')
      .replace('{coupon_code}', couponCode ?? ''),
  );
  const buttonText = useParsedHtmlString(videoPageInfo.button_text);

  const strings = useCaseInfo.language === 'SPANISH' ? LocalizedStrings.es : LocalizedStrings.en;

  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesDiv.current) {
        messagesDiv.current.lastElementChild?.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const promptUserToReply = async (source: 'heart' | 'videoEnd') => {
    if (isAbandonedCart) {
      setAnimateCirclePlay(false);
      setAnimateCTA(true);
      return;
    }
    if (
      !videoPageInfo.show_feedback_box ||
      didPromptUserToReply.current[source] ||
      userDidSendMessage.current
    ) {
      return;
    }
    didPromptUserToReply.current[source] = true;

    await sleep(200);
    setShowTypingIndicator(true);
    scrollToBottom();
    await sleep(1000);
    setShowTypingIndicator(false);
    setMessages((m) => [
      ...m,
      {
        type: 'received',
        messageType: 'string',
        message: source === 'heart' ? strings.heartSendMessage() : strings.videoSendMessage(),
      },
    ]);
    textAreaRef.current?.focus();
    scrollToBottom();
  };

  const onHeart = () => {
    setDidHeart((old) => !old);
    scrollToBottom();
    promptUserToReply('heart');
  };

  const appendUserSentMessages = async (message: string) => {
    setMessages((m) => [...m, { type: 'sent', messageType: 'string', message: message }]);
    scrollToBottom();

    if (userDidSendMessage.current) {
      return;
    }
    userDidSendMessage.current = true;
    await sleep(200);
    setShowTypingIndicator(true);
    scrollToBottom();
    await sleep(1000);
    setShowTypingIndicator(false);
    setMessages((m) => [
      ...m,
      {
        type: 'received',
        messageType: 'string',
        message:
          videoPageInfo.post_reply_text && videoPageInfo.post_reply_text != ''
            ? videoPageInfo.post_reply_text
            : strings.thanksForReply(),
      },
    ]);
    scrollToBottom();
  };

  const sendUserMessage = () => {
    if (textAreaValue === '') {
      return;
    }
    appendUserSentMessages(textAreaValue);
    setTextAreaValue('');

    postFeedback(hyperlinkVideoUrl, textAreaValue, email, phone, deliveryMethod ?? null);
  };

  const onTextAreaKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendUserMessage();
    }
  };

  return (
    <section className='mx-auto h-full min-h-[100dvh] bg-zinc-50'>
      <div className='mx-auto block h-full min-h-[100dvh] w-screen max-w-2xl bg-white'>
        <div
          className={cn(
            'fixed top-0 z-10 flex h-32 w-full max-w-2xl justify-center p-4 text-center',
            classes.message_header,
          )}
        >
          {storeInfo.logo && (
            <img
              alt='store logo'
              className={cn(
                { 'mix-blend-multiply': !storeInfo.logo.endsWith('.png') },
                'm-auto max-h-full max-w-full',
              )}
              src={storeInfo.logo}
            />
          )}
        </div>

        <div
          className={cn(classes.message_body, 'flex flex-col pb-20 pt-[9rem]')}
          ref={messagesDiv}
        >
          <ChatBubble type='received'>{strings.hello(customerFirstName)}</ChatBubble>
          {Boolean(messageText) && <ChatBubble type='received'>{messageText}</ChatBubble>}
          <ChatBubble type='received' className='w-full'>
            <VideoPosterMessage
              onClick={() => {
                setOpenVideoModal(true);
              }}
              onHeart={onHeart}
              didHeart={didHeart}
              animateCirclePlay={animateCirclePlay}
              posterImage={videoPageInfo.poster_img_url ?? ''}
            />
          </ChatBubble>
          {Boolean(supportingText) && <ChatBubble type='received'>{supportingText}</ChatBubble>}
          {productImages !== undefined &&
            Boolean(productImages.product_images.length) &&
            (ctaUrl ?? videoPageInfo.cta_url) && (
              <ChatBubble type='received'>
                <ProductImagesMessage
                  productImageUrl1={productImages.product_images[0]}
                  productImageUrl2={
                    productImages.product_images.length > 1
                      ? productImages.product_images[1]
                      : undefined
                  }
                  href={ctaUrl ?? videoPageInfo.cta_url}
                  onClick={() => posthog.capture('ctaClicked', posthogData)}
                />
              </ChatBubble>
            )}
          {isAbandonedCart && Boolean(buttonText) && (ctaUrl ?? videoPageInfo.cta_url) && (
            <div className='mb-4 pl-3'>
              <Button
                className='h-16 max-w-[255px] border-2 !no-underline'
                style={{ backgroundColor: videoPageInfo.button_color }}
                href={ctaUrl ?? videoPageInfo.cta_url}
                onClick={() => posthog.capture('ctaClicked', posthogData)}
                theme={{ inner: { base: 'w-full' } }}
              >
                <div className='flex w-full justify-between text-xl'>
                  <p style={{ color: videoPageInfo.button_text_color }}>{buttonText}</p>
                  <FontAwesomeIcon
                    className={animateCTA ? 'fa-beat' : ''}
                    size='xl'
                    icon={solid('chevron-right')}
                    style={{ color: videoPageInfo.button_text_color }}
                  />
                </div>
              </Button>
            </div>
          )}
          {!isAbandonedCart && Boolean(buttonText) && (ctaUrl ?? videoPageInfo.cta_url) && (
            <ChatBubble type='received'>
              <CtaMessage
                message={buttonText}
                href={ctaUrl ?? videoPageInfo.cta_url}
                onClick={() => posthog.capture('ctaClicked', posthogData)}
              />
            </ChatBubble>
          )}
          {messages.map((message, index) => (
            <ChatBubble type={message.type} className={message.className} key={index}>
              {message.messageType === 'string' && message.message}
              {message.messageType === 'cta' && (
                <CtaMessage
                  message={message.message}
                  href={message.href}
                  onClick={() => posthog.capture('ctaClicked', posthogData)}
                />
              )}
            </ChatBubble>
          ))}
          {showTypingIndicator && (
            <ChatBubble type='received'>
              <div className='flex h-5 items-center justify-center'>
                <div className={classes.typingIndicatorBubbleDot}></div>
                <div className={classes.typingIndicatorBubbleDot}></div>
                <div className={classes.typingIndicatorBubbleDot}></div>
              </div>
            </ChatBubble>
          )}
        </div>

        {videoPageInfo.show_feedback_box && (
          <div className='fixed bottom-0 flex w-full max-w-2xl items-center bg-gray-50 px-1 py-2 text-center'>
            <HeartVideoIcon didHeart={didHeart} onHeart={onHeart}></HeartVideoIcon>
            <div className='relative mr-1 w-full'>
              <textarea
                id='chat'
                rows={1}
                ref={textAreaRef}
                className='mx-1 block w-full resize-none rounded-full border border-gray-300 bg-white p-2.5 px-5 text-base text-gray-900 focus:border-blue-500 focus:ring-blue-500'
                placeholder={strings.replyPlaceholder(useCaseInfo.speaker_name!)}
                value={textAreaValue}
                onChange={(e) => setTextAreaValue(e.target.value)}
                onKeyDown={onTextAreaKeyDown}
              />
              {textAreaValue !== '' && (
                <button
                  className='text-md absolute bottom-0 right-0 top-0 my-auto h-8 cursor-pointer justify-center rounded-full bg-blue-600 px-2.5 py-1 text-white hover:bg-blue-800'
                  onClick={sendUserMessage}
                >
                  <FontAwesomeIcon icon={solid('arrow-up')} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      <Modal
        dismissible
        popup
        position='center'
        size='lg'
        show={openVideoModal}
        onClose={() => {
          if (videoPlayer.current && !videoPlayer.current.paused()) {
            videoPlayer.current.pause();
          }
          setOpenVideoModal(false);
          scrollToBottom();
          setTimeout(() => promptUserToReply('videoEnd'), 500);
        }}
      >
        <div
          className='absolute left-0 top-0 z-20 cursor-pointer p-4'
          onClick={() => {
            if (videoPlayer.current && !videoPlayer.current.paused()) {
              videoPlayer.current.pause();
            }
            setOpenVideoModal(false);
            scrollToBottom();
            setTimeout(() => promptUserToReply('videoEnd'), 500);
          }}
        >
          <FontAwesomeIcon
            icon={solid('circle-xmark')}
            className='cursor-pointer justify-center rounded-full text-4xl text-white'
          />
        </div>
        <VideoJS
          options={{
            fill: true,
            controls: true,
            autoplay: true,
            poster: videoPageInfo.poster_img_url ?? '',
            sources: [{ src: object.video_url, type: 'video/mp4' }],
            controlBar: {
              fullscreenToggle: false,
              pictureInPictureToggle: false,
            },
            userActions: {
              doubleClick: false,
            },
            html5: {
              playsinline: true,
            },
          }}
          className='h-[95dvh]'
          onReady={(player) => {
            player.on('play', function () {
              posthog.capture('customerVideo_videojsplay', posthogData);
              setAnimateCTA(true);
            });
            player.on('pause', function () {
              posthog.capture('customerVideo_videojspause', posthogData);
            });
            player.on('canplay', function () {
              posthog.capture('customerVideo_videojscanplay', posthogData);
            });
            player.on('canplaythrough', function () {
              posthog.capture('customerVideo_videojscanplaythrough', posthogData);
            });
            player.on('ended', function () {
              posthog.capture('customerVideo_videojsended', posthogData);
              setOpenVideoModal(false);
              setTimeout(() => promptUserToReply('videoEnd'), 500);
            });
            player.on('loadeddata', function () {
              posthog.capture('customerVideo_videojsloadeddata', posthogData);
            });
            videoPlayer.current = player;
          }}
        />
      </Modal>
    </section>
  );
}
